import { sendMessage } from '../../../common/message/sendMessage'
import { listenMessage } from '../../../common/message/listenMessage'
import { localStorage as createLocalStorage } from 'helpers/localStorage'

import {
  requestThemeSyncAction,
  THEME_STORAGE_KEY,
  THEME_CHANGE_EVENT,
} from './actions'

export * from './actions'

export enum Theme {
  LIGHT = 'light',
  DARK = 'dark',
}

const storage = createLocalStorage()

export const isTheme = (theme: unknown): theme is Theme =>
  theme === Theme.LIGHT || theme === Theme.DARK

export const applyTheme = (theme: string | null) => {
  const resolvedTheme = theme === Theme.DARK ? Theme.DARK : Theme.LIGHT
  document.documentElement.classList.toggle(
    Theme.DARK,
    resolvedTheme === Theme.DARK,
  )
}

export const requestThemeSync = () =>
  sendMessage({
    action: requestThemeSyncAction(undefined),
  })

type ThemeSyncMessage = { type: string; theme?: Theme }

type SubscribeThemeSyncProps = {
  storageKey: string
  changeEventType: string
  onThemeChange: (theme: string | null) => void
  isValidTheme?: (theme: unknown) => boolean
}

let activeThemeSyncConsumers = 0
let unsubscribeThemeSyncShared: null | (() => void) = null

export const subscribeThemeSync = ({
  storageKey,
  changeEventType,
  onThemeChange,
  isValidTheme,
}: SubscribeThemeSyncProps) => {
  const handleStorage = (event: StorageEvent) => {
    if (event.key === storageKey) {
      onThemeChange(event.newValue)
    }
  }

  window.addEventListener('storage', handleStorage)

  const unsubscribeMessage = listenMessage<ThemeSyncMessage>({
    whitelist: [{ type: changeEventType }],
    onMessage: (action) => {
      if (!('theme' in action)) {
        return
      }

      const { theme } = action
      if (!isValidTheme?.(theme)) {
        return
      }

      onThemeChange(theme as string)
    },
  })

  return () => {
    window.removeEventListener('storage', handleStorage)
    unsubscribeMessage()
  }
}

export const initializeThemeSync = () => {
  if (typeof window === 'undefined') {
    return () => undefined
  }

  activeThemeSyncConsumers += 1

  if (activeThemeSyncConsumers === 1) {
    const initialTheme: string | null =
      storage.getItem(THEME_STORAGE_KEY) ?? null
    applyTheme(initialTheme)

    unsubscribeThemeSyncShared = subscribeThemeSync({
      storageKey: THEME_STORAGE_KEY,
      changeEventType: THEME_CHANGE_EVENT,
      onThemeChange: applyTheme,
      isValidTheme: isTheme,
    })
    requestThemeSync()
  }

  return () => {
    activeThemeSyncConsumers = Math.max(0, activeThemeSyncConsumers - 1)

    if (activeThemeSyncConsumers === 0) {
      unsubscribeThemeSyncShared?.()
      unsubscribeThemeSyncShared = null
    }
  }
}
